import { FC, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'next-i18next';
import { Form } from 'antd';

import { ButtonIw } from '@/_pages/interwill/components/ButtonIw';
import { FieldContainerIw } from '@/_pages/interwill/components/FieldContainerIw';
import { InterwillCreateEntityOnSubmitType } from '@/_pages/interwill/hooks/useInterwillCreateEntity';
import { Input } from '@/shared/components';
import { SelectCountry } from '@/shared/components/forms/fields/SelectCountry';
import { useIsDesktop } from '@/shared/hooks/use-is-desktop';
import { CollectionName } from '@/shared/lib/sj-orm/constants';
import { useSJDatabase } from '@/shared/lib/sj-orm/hooks/use-sj-database';
import { DocumentType } from '@/shared/lib/sj-orm/models/document/document-base.dto';
import {
  PersonalIdentifierDocumentDto,
  PersonalIdentifierType,
} from '@/shared/lib/sj-orm/models/document/personal-identifier.document.dto';

import {
  InterwillFieldsLineWrapper,
  InterwillFormContainer,
  InterwillStyledForm,
  InterwillSubmitButtonWrapper,
} from '../styled';

import { InterwillSelectDocumentTypeForm } from './InterwillSelectDocumentTypeForm';
import { InterwillDocumentFormType, InterwillPersonalIdFormParams } from './types';

type InterwillPersonalIdFormProps = {
  onSubmit: InterwillCreateEntityOnSubmitType;
  setDocumentFormType: (document: InterwillDocumentFormType) => void;
};

export const InterwillPersonalIdForm: FC<InterwillPersonalIdFormProps> = ({
  onSubmit,
  setDocumentFormType,
}) => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'addEntity.document' });
  const { t: td } = useTranslation('documents', { keyPrefix: 'enums.docCategory' });
  const isDesktop = useIsDesktop();
  const [formType, setFormType] = useState<InterwillDocumentFormType>();

  const {
    control,
    handleSubmit: formHandleSubmit,
    formState: { isSubmitting },
  } = useForm<InterwillPersonalIdFormParams>();

  const sjDatabase = useSJDatabase();

  const handleSubmit = async (data: InterwillPersonalIdFormParams) => {
    if (!formType) {
      return;
    }

    const documentDto: PersonalIdentifierDocumentDto = {
      id: sjDatabase.generateId(CollectionName.PERSONAL_IDENTIFIERS_DOCUMENTS),
      type: DocumentType.PERSONAL_IDENTIFIERS,
      personalIdentifierType: formType,
      country: data.country,
      name: data.name,
      surnames: data.surnames,
      names: data.names,
      dateOfIssue: data.dateOfIssue,
      endDate: data.endDate,
      issuedBy: data.issuedBy,
      number: data.number,
      accessNumber: data.cardAccessNumber,
      beneficiaryId: 'me',
    };

    await onSubmit({ collection: CollectionName.PERSONAL_IDENTIFIERS_DOCUMENTS, dto: documentDto });
  };

  if (!formType) {
    return (
      <InterwillSelectDocumentTypeForm
        onConfirm={(_formType) => setFormType(_formType)}
        setDocumentFormType={setDocumentFormType}
      />
    );
  }

  return (
    <>
      <InterwillStyledForm onFinish={formHandleSubmit(handleSubmit)}>
        <InterwillFormContainer>
          <Controller
            name={'name'}
            control={control}
            rules={{ required: t('fields.name.error') }}
            render={({ field, fieldState }) => {
              return (
                <FieldContainerIw
                  required
                  label={t(`fields.${field.name}.title`)}
                  tooltip={t(`fields.${field.name}.tooltip`)}
                >
                  <Form.Item
                    validateStatus={fieldState.error ? 'error' : ''}
                    help={fieldState.error?.message}
                  >
                    <Input
                      placeholder={t(`fields.${field.name}.placeholder`)}
                      type={'text'}
                      {...field}
                    />
                  </Form.Item>
                </FieldContainerIw>
              );
            }}
          />

          <InterwillFieldsLineWrapper>
            <Controller
              name={'names'}
              control={control}
              rules={{ required: t('fields.names.error') }}
              render={({ field, fieldState }) => {
                return (
                  <FieldContainerIw required label={t(`fields.${field.name}.title`)}>
                    <Form.Item
                      validateStatus={fieldState.error ? 'error' : ''}
                      help={fieldState.error?.message}
                    >
                      <Input
                        placeholder={t(`fields.${field.name}.placeholder`)}
                        type={'text'}
                        {...field}
                      />
                    </Form.Item>
                  </FieldContainerIw>
                );
              }}
            />

            <Controller
              name={'surnames'}
              control={control}
              rules={{ required: t('fields.surnames.error') }}
              render={({ field, fieldState }) => {
                return (
                  <FieldContainerIw required label={t(`fields.${field.name}.title`)}>
                    <Form.Item
                      validateStatus={fieldState.error ? 'error' : ''}
                      help={fieldState.error?.message}
                    >
                      <Input
                        placeholder={t(`fields.${field.name}.placeholder`)}
                        type={'text'}
                        {...field}
                      />
                    </Form.Item>
                  </FieldContainerIw>
                );
              }}
            />
          </InterwillFieldsLineWrapper>

          <Controller
            name={'country'}
            control={control}
            rules={{ required: t('fields.country.error') }}
            render={({ field, fieldState }) => {
              return (
                <FieldContainerIw required label={t(`fields.${field.name}.title`)}>
                  <Form.Item
                    validateStatus={fieldState.error ? 'error' : ''}
                    help={fieldState.error?.message}
                  >
                    <SelectCountry placeholder={t(`fields.${field.name}.placeholder`)} {...field} />
                  </Form.Item>
                </FieldContainerIw>
              );
            }}
          />

          <Controller
            name={'citizenship'}
            control={control}
            rules={{ required: false }}
            render={({ field, fieldState }) => {
              return (
                <FieldContainerIw label={t(`fields.${field.name}.title`)}>
                  <Form.Item
                    validateStatus={fieldState.error ? 'error' : ''}
                    help={fieldState.error?.message}
                  >
                    <SelectCountry placeholder={t(`fields.${field.name}.placeholder`)} {...field} />
                  </Form.Item>
                </FieldContainerIw>
              );
            }}
          />

          <Controller
            name={'number'}
            control={control}
            rules={{ required: false }}
            render={({ field, fieldState }) => {
              return (
                <FieldContainerIw label={t(`fields.${field.name}.title`)}>
                  <Form.Item
                    validateStatus={fieldState.error ? 'error' : ''}
                    help={fieldState.error?.message}
                  >
                    <Input
                      placeholder={t(`fields.${field.name}.placeholder`)}
                      type={'text'}
                      {...field}
                    />
                  </Form.Item>
                </FieldContainerIw>
              );
            }}
          />

          {formType === PersonalIdentifierType.NATIONAL_ID && (
            <Controller
              name={'cardAccessNumber'}
              control={control}
              rules={{ required: false }}
              render={({ field, fieldState }) => {
                return (
                  <FieldContainerIw
                    label={t(`fields.${field.name}.title`)}
                    tooltip={t(`fields.${field.name}.tooltip`)}
                  >
                    <Form.Item
                      validateStatus={fieldState.error ? 'error' : ''}
                      help={fieldState.error?.message}
                    >
                      <Input
                        placeholder={t(`fields.${field.name}.placeholder`)}
                        type={'text'}
                        {...field}
                      />
                    </Form.Item>
                  </FieldContainerIw>
                );
              }}
            />
          )}

          <InterwillFieldsLineWrapper>
            <Controller
              name={'dateOfIssue'}
              control={control}
              rules={{ required: false }}
              render={({ field, fieldState }) => {
                return (
                  <FieldContainerIw label={t(`fields.${field.name}.title`)}>
                    <Form.Item
                      validateStatus={fieldState.error ? 'error' : ''}
                      help={fieldState.error?.message}
                    >
                      <Input
                        type={'date'}
                        placeholder={t(`fields.${field.name}.placeholder`)}
                        {...field}
                      />
                    </Form.Item>
                  </FieldContainerIw>
                );
              }}
            />

            <Controller
              name={'endDate'}
              control={control}
              rules={{ required: false }}
              render={({ field, fieldState }) => {
                return (
                  <FieldContainerIw label={t(`fields.${field.name}.title`)}>
                    <Form.Item
                      validateStatus={fieldState.error ? 'error' : ''}
                      help={fieldState.error?.message}
                    >
                      <Input
                        type={'date'}
                        placeholder={t(`fields.${field.name}.placeholder`)}
                        {...field}
                      />
                    </Form.Item>
                  </FieldContainerIw>
                );
              }}
            />
          </InterwillFieldsLineWrapper>

          {formType === PersonalIdentifierType.PASSPORT && (
            <Controller
              name={'issuedBy'}
              control={control}
              rules={{ required: false }}
              render={({ field, fieldState }) => {
                return (
                  <FieldContainerIw label={t(`fields.${field.name}.title`)}>
                    <Form.Item
                      validateStatus={fieldState.error ? 'error' : ''}
                      help={fieldState.error?.message}
                    >
                      <Input
                        placeholder={t(`fields.${field.name}.placeholder`)}
                        type={'text'}
                        {...field}
                      />
                    </Form.Item>
                  </FieldContainerIw>
                );
              }}
            />
          )}
        </InterwillFormContainer>

        <InterwillSubmitButtonWrapper>
          <ButtonIw
            htmlType="submit"
            block={!isDesktop}
            disabled={isSubmitting}
            loading={isSubmitting}
          >
            {t('submitButtonText', {
              documentName:
                formType === PersonalIdentifierType.NATIONAL_ID ? td('nationalId') : td('passport'),
            })}
          </ButtonIw>
        </InterwillSubmitButtonWrapper>
      </InterwillStyledForm>
    </>
  );
};
